import Card from '@/global/components/Card'
import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import { SelectField } from '@/global/components/SelectField'
import Skeleton from '@/global/components/Skeleton'
import { cn } from '@/lib/utils'
import { usePlanosContext } from '@/pages/Planos.context'
import { PlanoFixo } from '@/service/api'
import { Button } from 'global/components/Button'
import { memo, type ReactNode, useEffect, useState } from 'react'

type Props = {
    planos: PlanoFixo[]
    trocar?: boolean
}

export const PlanosCard = memo<Props>(({ planos, trocar }) => {
    const hasPlanos = planos.length > 0

    const [planosMaisCem, planosMenosCem] = [
        planos.filter(p => p.limiteFuncionarios >= 100),
        planos.filter(p => p.limiteFuncionarios < 100),
    ]
    const [planoSelecionado, setPlano] = useState<PlanoFixo>()

    useEffect(() => {
        if (!planoSelecionado && planosMaisCem.at(0)) setPlano(planosMaisCem[0])
    }, [planosMaisCem])

    return (
        <div
            className={cn(
                'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
                'gap-6 p-4',
                'overflow-y-auto max-h-full h-full',
                'justify-items-center',
            )}
        >
            {[
                hasPlanos
                    ? planosMenosCem?.map((plano, index) => {
                          return (
                              <CardPlan
                                  key={index}
                                  trocar={trocar}
                                  limiteFuncionarios={plano.limiteFuncionarios}
                                  periodicity={plano.periodicity}
                                  preco={+plano.price}
                              />
                          )
                      })
                    : Array.from({ length: 3 }).map((_, index) => (
                          <div className="w-[300px]" key={index}>
                              <Card className="rounded-xl h-[430px]">
                                  <Skeleton />
                              </Card>
                          </div>
                      )),
                !!planoSelecionado && (
                    <CardPlan
                        key="plano-selecionado"
                        trocar={trocar}
                        limiteFuncionarios={planoSelecionado.limiteFuncionarios}
                        periodicity={planoSelecionado.periodicity}
                        preco={+planoSelecionado.price}
                        cabecalho={
                            <SelectField
                                variant="outlined"
                                label="Precisa de mais funcionários?"
                                value={planoSelecionado.limiteFuncionarios}
                                onValueChange={limite =>
                                    setPlano(
                                        planosMaisCem.find(
                                            p =>
                                                p.limiteFuncionarios === limite,
                                        )!,
                                    )
                                }
                                options={planosMaisCem?.map(plano => ({
                                    value: plano.limiteFuncionarios,
                                    label: `até ${plano.limiteFuncionarios}\u00A0funcionários`,
                                }))}
                            />
                        }
                    />
                ),
            ]}
        </div>
    )
})

function CardPlan({
    trocar,
    limiteFuncionarios,
    periodicity,
    preco,
    cabecalho,
}: Pick<PlanoFixo, 'limiteFuncionarios' | 'periodicity'> & {
    trocar?: boolean
    preco: number
    /** Substitui o título do cartão — o cartão das faixas usa um select. */
    cabecalho?: ReactNode
}) {
    const { PlanoDiferenciado } = usePlanosContext()

    const name = (() => {
        if (limiteFuncionarios == 0 && PlanoDiferenciado) {
            return `até ${PlanoDiferenciado.limiteFuncionarios} funcionários`
        } else if (limiteFuncionarios != 0) {
            return `até ${limiteFuncionarios} funcionários`
        } else {
            return `Plano especial`
        }
    })()

    return (
        <div className="w-[300px]">
            <Card className="rounded-xl min-h-[430px] flex flex-col justify-evenly">
                {cabecalho ?? (
                    <div className="flex justify-center items-center py-4 rounded-md">
                        <h6 className="text-lg font-semibold flex justify-center items-center">
                            <strong>{name}</strong>
                        </h6>
                    </div>
                )}
                <DividerDescontoPlano
                    periodicity={periodicity}
                    isDiferenciado={limiteFuncionarios == 0}
                />
                <div
                    className={cn(
                        'flex flex-col justify-center items-center',
                        ['yearly', 'quarterly'].includes(periodicity)
                            ? 'my-10'
                            : 'my-12',
                    )}
                >
                    <PrecoPlano preco={preco} />
                </div>
                <DividerPeriodoPlano periodicity={periodicity} />
                <br />
                <div className="flex justify-center items-center px-4">
                    <BotaoContratarPlano
                        limiteFuncionarios={limiteFuncionarios}
                        periodicity={periodicity}
                        trocar={trocar}
                    />
                </div>
                <PeriodoRenovacaoPlano periodicity={periodicity} />
            </Card>
        </div>
    )
}

function PrecoPlano({ preco }: { preco: number }) {
    return (
        <div className="flex justify-center items-baseline flex-wrap [overflow-wrap:anywhere]">
            <h4 className="text-3xl font-bold -ml-4">R$</h4>
            <h2 className="text-6xl font-bold">
                {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                })
                    .format(preco / 100)
                    .split(',')[0]
                    .replace('R$', '')}
            </h2>
            <h6 className="text-xl font-semibold">
                ,{(preco / 100).toFixed(2).split('.')[1]}
            </h6>
        </div>
    )
}

function DividerPeriodoPlano({ periodicity }: Pick<PlanoFixo, 'periodicity'>) {
    const mesAno = periodicity === 'monthly' ? 'mês' : 'ano'

    return (
        <HorizontalDivider>
            <span className="text-base font-medium">
                <strong>POR {mesAno.toUpperCase()}</strong>
            </span>
        </HorizontalDivider>
    )
}

function PeriodoRenovacaoPlano({
    periodicity,
}: Pick<PlanoFixo, 'periodicity'>) {
    const mesAno =
        periodicity === 'monthly'
            ? 'mês'
            : periodicity === 'quarterly'
              ? 'trimestre'
              : 'ano'

    return (
        <div className="px-4 mt-4">
            <span className="text-sm text-muted-foreground text-center block">
                Este plano é automaticamente renovado a cada {mesAno}
            </span>
        </div>
    )
}

const DividerDescontoPlano = ({
    periodicity,
    isDiferenciado,
}: Pick<PlanoFixo, 'periodicity'> & { isDiferenciado: boolean }) => {
    if (isDiferenciado)
        return <HorizontalDivider className="mx-4 w-[calc(100%-2rem)]" />
    return (
        <HorizontalDivider className="text-warning">
            {periodicity === 'yearly' && '15% desconto'}
            {periodicity === 'quarterly' && '10% desconto'}
        </HorizontalDivider>
    )
}

function BotaoContratarPlano({
    limiteFuncionarios,
    periodicity,
    trocar,
}: Pick<PlanoFixo, 'limiteFuncionarios' | 'periodicity'> & {
    trocar?: boolean
}) {
    return (
        <Button
            size="large"
            fullWidth
            href={
                trocar
                    ? `/planos/alterar?p=${limiteFuncionarios}-${periodicity}`
                    : `/planos/checkout?p=${limiteFuncionarios}-${periodicity}`
            }
        >
            {trocar ? 'Alterar plano' : 'Contratar plano'}
        </Button>
    )
}
